<template>
  <div
    :class="[
      'bg-teal-700 text-white rounded-lg shadow-lg min-w-[220px] border-2 transition-all relative',
      selected ? 'border-cyan-400 shadow-cyan-400/50' : data.__validationError ? 'border-red-500' : 'border-teal-800'
    ]"
  >
    <!-- Header -->
    <div class="bg-teal-800 px-3 py-2 rounded-t-lg flex items-center gap-2 justify-between">
      <div class="flex items-center gap-2">
        <span class="material-icons text-sm">psychology</span>
        <span class="font-semibold text-sm">{{ $t('flowEditor.nodes.intentVerifierNode.title') }}</span>
      </div>
      <ValidationMarker :error="data.__validationError" />
    </div>

    <!-- Body -->
    <div class="px-3 py-3 flex flex-col gap-2 text-sm">
      <div class="text-teal-200 text-xs flex items-center gap-1">
        <span class="material-icons" style="font-size: 14px;">input</span>
        <span class="truncate" v-tooltip="data.sourceVariable || 'trigger_intent'">
          {{ data.sourceVariable || 'trigger_intent' }}
        </span>
      </div>
      <div class="text-teal-200 text-xs flex items-center gap-1">
        <span class="material-icons" style="font-size: 14px;">output</span>
        <span class="truncate" v-tooltip="data.targetVariable || 'detected_intent'">
          {{ data.targetVariable || 'detected_intent' }}
        </span>
      </div>

      <div class="text-xs text-teal-200 mt-2 pt-2 border-t border-teal-600">
        <div class="font-medium mb-1">{{ $t('flowEditor.nodes.intentVerifierNode.intentsLabel') }}</div>
        <div
          v-for="(intent, idx) in data.intents"
          :key="idx"
          class="truncate transition-all"
          :class="{ 'bg-cyan-500/30 font-semibold': hoveredIntent === intent.id }"
          @mouseenter="hoveredIntent = intent.id"
          @mouseleave="hoveredIntent = null"
        >
          {{ idx + 1 }}. {{ intent.label }}
        </div>
        <div
          class="text-teal-300 italic mt-1 transition-all"
          :class="{ 'bg-gray-500/30 font-semibold': hoveredIntent === 'no_match' }"
          @mouseenter="hoveredIntent = 'no_match'"
          @mouseleave="hoveredIntent = null"
        >
          {{ $t('flowEditor.nodes.intentVerifierNode.noMatchLabel') }}
        </div>
      </div>
    </div>

    <!-- Target Handle (input, left) -->
    <Handle
      type="target"
      :position="Position.Left"
      class="w-3 h-3 !bg-teal-400 !border-2 !border-white"
    />

    <!-- Dynamic Source Handles (output, right) with labels -->
    <div
      v-for="(intent, idx) in (data.intents || [])"
      :key="intent.id"
      class="absolute flex items-center gap-1"
      :style="{ top: `${calculateHandleTop(idx)}px`, right: '-8px' }"
      @mouseenter="hoveredIntent = intent.id"
      @mouseleave="hoveredIntent = null"
    >
      <Handle
        :id="`handle_${intent.id}`"
        type="source"
        :position="Position.Right"
        :class="[
          'w-3 h-3 !border-2 !border-white transition-all',
          hoveredIntent === intent.id ? '!bg-cyan-300 !w-4 !h-4 !shadow-lg !shadow-cyan-400/50' : '!bg-cyan-400'
        ]"
        v-tooltip.top="intent.label"
      />
      <span
        class="text-[10px] bg-teal-900/90 px-1 rounded-sm whitespace-nowrap pointer-events-none transition-opacity"
        :class="hoveredIntent === intent.id ? 'opacity-100' : 'opacity-70'"
      >
        {{ idx + 1 }}
      </span>
    </div>
    <div
      class="absolute flex items-center gap-1"
      :style="{ top: `${calculateHandleTop((data.intents || []).length)}px`, right: '-8px' }"
      @mouseenter="hoveredIntent = 'no_match'"
      @mouseleave="hoveredIntent = null"
    >
      <Handle
        id="handle_no_match"
        type="source"
        :position="Position.Right"
        :class="[
          'w-3 h-3 !border-2 !border-white transition-all',
          hoveredIntent === 'no_match' ? '!bg-gray-300 !w-4 !h-4 !shadow-lg !shadow-gray-400/50' : '!bg-gray-400'
        ]"
        v-tooltip.top="$t('flowEditor.nodes.intentVerifierNode.noMatchHandleTitle')"
      />
      <span
        class="text-[10px] bg-teal-900/90 px-1 rounded-sm whitespace-nowrap pointer-events-none transition-opacity"
        :class="hoveredIntent === 'no_match' ? 'opacity-100' : 'opacity-70'"
      >
        {{ $t('flowEditor.nodes.intentVerifierNode.noMatchHandleLabel') }}
      </span>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import { Handle, Position } from '@vue-flow/core';
import ValidationMarker from './ValidationMarker.vue';

defineProps({
  data: {
    type: Object,
    required: true,
  },
  selected: {
    type: Boolean,
    default: false,
  },
});

const hoveredIntent = ref(null);

const calculateHandleTop = (index) => {
  const headerHeight = 32;
  const bodyPadding = 12;
  const handleSpacing = 28;
  return headerHeight + bodyPadding + (index * handleSpacing);
};
</script>
